import { z } from 'zod';
import type { ConfigTahiti } from '../config';
import { ServiceIndisponibleError } from '../erreurs';
import type { ContexteRosetta } from './contexte';
import {
  CODE_LANGUE,
  idSchema,
  inserer,
  lireLignes,
  texteLocaliseSchema,
  texteMultilingue,
} from './h2data';
import type { AppelHttp } from './http';

/**
 * The `rose_document` calls: the source text of a translation lives in H2Data.
 *
 * Title given to the document (and to the task) when the user typed none: Tahiti shows
 * the title in its own screens, an empty one would be an anonymous record. It is read
 * back as "no title".
 */
export const TITRE_PAR_DEFAUT = 'Rosetta - sans titre';

const texteMultilingueSchema = z
  .record(z.string(), z.object({ ai: z.literal(false), value: z.string().min(1) }))
  .refine((valeur) => Object.keys(valeur).length > 0, 'at least one language');

/**
 * The creation request of the reference script: the title and the source text are
 * multilingual fields (`{ <lang>: { ai: false, value } }`), the languages are identifiers
 * resolved by code (`contexte.ts`) and `main_lang` is the code of the language of the text.
 *
 * Rosetta rewrites a French text in French: the source and the target language are both
 * French, and the text is written in French only (the script writes it in `fr`, `ru` and
 * `en` because its default target is Russian).
 *
 * TO VALIDATE(#6): that H2Data accepts the multilingual fields with the French entry only,
 * and fills the system fields of an `h2_entry` itself (the script sends none either).
 */
export const requeteCreationDocumentSchema = z.object({
  title: texteMultilingueSchema,
  source_text: texteMultilingueSchema,
  source_language_id: z.number().int().positive(),
  target_language_id: z.number().int().positive(),
  main_lang: z.literal(CODE_LANGUE),
  h2_workspace_id: z.number().int().positive(),
});

export const ligneDocumentSchema = z.object({
  id: idSchema,
  title: texteLocaliseSchema.nullish(),
  source_text: texteLocaliseSchema.nullish(),
});

export interface DocumentSource {
  /** Absent when the user typed no title. */
  titre?: string;
  texte: string;
}

/** Creates the `rose_document` of a translation and returns its identifier. */
export async function creerDocument(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  contexte: ContexteRosetta,
  document: { titre?: string; texte: string },
): Promise<string> {
  const titre = document.titre?.trim();
  const data = requeteCreationDocumentSchema.parse({
    title: texteMultilingue(titre === undefined || titre === '' ? TITRE_PAR_DEFAUT : titre, [
      CODE_LANGUE,
    ]),
    source_text: texteMultilingue(document.texte, [CODE_LANGUE]),
    source_language_id: contexte.langueId,
    target_language_id: contexte.langueId,
    main_lang: CODE_LANGUE,
    h2_workspace_id: config.espaceTravailId,
  });
  return inserer(appel, config, jeton, { table: 'rose_document', data });
}

/** Reads the source text (and title) of a document. */
export async function lireDocument(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  id: string,
): Promise<DocumentSource> {
  const [ligne] = await lireLignes(
    appel,
    config,
    jeton,
    {
      table: 'rose_document',
      fields: ['id', 'title', 'source_text'],
      where: ['id', Number(id), '='],
      limit: 1,
      lang: CODE_LANGUE,
    },
    ligneDocumentSchema,
  );
  if (ligne === undefined || ligne.source_text === undefined || ligne.source_text === null) {
    throw new ServiceIndisponibleError(`The document ${id} of the translation cannot be read`);
  }

  const titre = ligne.title?.trim();
  return {
    texte: ligne.source_text,
    ...(titre === undefined || titre === '' || titre === TITRE_PAR_DEFAUT ? {} : { titre }),
  };
}
